import {
  stores,
} from 'mobx-store';
import {
  AccessLevel,
  User,
} from 'mobx-store/storeInterfaces/statisticsInterface';
import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  useHistory,
  useParams,
} from 'react-router';
import {
  ROLES,
} from 'components/NavBar/NavigationItems/routes';
import {
  translate,
} from 'helperFunctions/translation';
import AlertDialog from 'components/Dialog/alertDialog';
import {
  Button,
  Card,
} from '@material-ui/core';

import SnackBarComponent from 'components/SnackBarComponent/SnackBarComponent';
import UpdateMembershipIdModal from 'components/modals/updateMemberShipModal';
import UpdateWalletPhoneNumber from 'screens/updateWalletNumberScreen';
import AddOnlineAccumulation from 'screens/addOnlineAccumulationScreen';
import * as styles from './styles.module.scss';
import {
  UserButtonAction,
  UserDetailToDisplay,
} from './types';
import {
  ModalKeys,
} from './constants';
import UserPasswordModal from 'screens/UserPasswordModal';

const UserDetails: React.FC = () => {
  const {
    userId,
  } = useParams<{userId: string}>();

  const {
    push,
  } = useHistory();

  const [open, setOpen] = useState<{[key: string]: boolean}>();

  const [user, setUser] = useState<User>();

  const [actions, setActions] = useState<UserButtonAction []>();

  const [details, setDetails] = useState<UserDetailToDisplay[]>();

  const {
    statisticsStore,
    userManagementStore,
    authStore,
  } = stores;

  const getUserDetails = async () => {
    const userDetails = statisticsStore.usersList.find((u) => u.userId === +userId);
    if (userDetails) {
      setUser(userDetails);
    } else {
      await userManagementStore.getUserDetails(+userId);
      setUser(userManagementStore.userDetails);
    }
  };

  useEffect(() => {

    getUserDetails();
  }, [userId, statisticsStore.usersList, userManagementStore]);

  const setModalState = (key: ModalKeys, val: boolean) => {
    setOpen((prev) => ({
      ...prev,
      [key]: val,
    }));
  };

  const handleAgreeSync = async () => {
    if (!user?.membershipId) {
      return;
    }
    try {
      await authStore.checkUserKYCStatus(user?.membershipId);
      SnackBarComponent.getInstance().openSnackbar(translate('updatedMessage', {
      }), 8000, 'success');
    } catch (error) {
      SnackBarComponent.getInstance().openSnackbar(error.response.data.message || translate('errorMessage', {
      }), 8000, 'error');
    } finally {
      setModalState(ModalKeys.KYC, false);
    }
  };

  const handleOpenModal = async () => {
    try {
      await authStore.softDeleteMembershipId(user!.userId);
      getUserDetails();
      SnackBarComponent.getInstance().openSnackbar(translate('updatedMessage'), 4000, 'success');
    } catch (error) {
      SnackBarComponent.getInstance().openSnackbar(
        error.response.data.message || translate('errorMessage'), 4000, 'error',
      );
    } finally {
      setModalState(ModalKeys.EDIT_USER, false);
    }
  };

  const handleCreateWalletUser = useCallback(async () => {
    try {
      const message = await authStore?.createWalletUser(+userId);
      return SnackBarComponent.getInstance().openSnackbar(
        message || translate('updatedMessage'), 4000, 'info',
      );
    } catch (error) {
      return SnackBarComponent.getInstance().openSnackbar(
        error.response.data.message || translate('errorMessage'), 4000, 'error',
      );
    }
  }, [authStore, userId]);

  const deleteUserCnfm = async () => {
    try {
      const message = await authStore?.deleteUser(+userId);
      return SnackBarComponent.getInstance().openSnackbar(message || translate('deletedMessage'), 4000, 'info');
    } catch (error) {
      return SnackBarComponent.getInstance().openSnackbar(
        error.response.data.message || translate('errorMessage'), 4000, 'error',
      );
    } finally {
      setModalState(ModalKeys.DELETE_USER, false);
    }
  };

  useEffect(() => {
    setActions([
      {
        btnText: translate('syncUserStatus'),
        hasRoles: [ROLES.SUPER_ADMIN, ROLES.DEV_ADMIN, ROLES.ADMIN, ROLES.CUSTOMER_SERVICE],
        onClick: () => setModalState(ModalKeys.KYC, true),
        disabled: user?.accessLevel === AccessLevel.full,
        btnLabel: 'Access level',
      },
      {
        btnText: translate('wifiPayment'),
        hasRoles: [ROLES.SUPER_ADMIN, ROLES.DEV_ADMIN, ROLES.ADMIN, ROLES.CUSTOMER_SERVICE],
        onClick: () => push({
          pathname: `/users/${userId}/wifi-packages`,
        }),
        disabled: false,
        btnLabel: 'WiFi packages',
      },
      {
        btnText: translate('editButton'),
        hasRoles: [ROLES.SUPER_ADMIN, ROLES.DEV_ADMIN, ROLES.ADMIN, ROLES.CUSTOMER_SERVICE],
        onClick: () => setModalState(ModalKeys.EDIT_USER, true),
        disabled: !!user?.isMainMember,
        btnLabel: 'Separate Membership ID',
      },
      {
        btnText: translate('createWallet'),
        hasRoles: [ROLES.SUPER_ADMIN, ROLES.DEV_ADMIN, ROLES.ADMIN, ROLES.CUSTOMER_SERVICE],
        onClick: () => handleCreateWalletUser(),
        disabled: false,
        btnLabel: 'Create wallet',
      },
      {
        btnText: translate('delete'),
        hasRoles: [ROLES.SUPER_ADMIN, ROLES.DEV_ADMIN, ROLES.ADMIN, ROLES.CUSTOMER_SERVICE],
        onClick: () => setModalState(ModalKeys.DELETE_USER, true),
        disabled: user?.accessLevel === AccessLevel.full,
        btnLabel: 'Delete user',
      },
      {
        btnText: translate('updatePhone'),
        hasRoles: [ROLES.SUPER_ADMIN, ROLES.DEV_ADMIN, ROLES.CUSTOMER_SERVICE],
        onClick: () => setModalState(ModalKeys.UPDATE_PHONE, true),
        disabled: false,
        btnLabel: '',
      },
      {
        disabled: false,
        btnText: translate('addAccumulation'),
        hasRoles: [ROLES.SUPER_ADMIN, ROLES.DEV_ADMIN],
        onClick: () => setModalState(ModalKeys.ACCUMULATION, true),
        btnLabel: '',
      },
      {
        btnText: translate('renewalsDetails'),
        hasRoles: [ROLES.SUPER_ADMIN, ROLES.DEV_ADMIN, ROLES.CUSTOMER_SERVICE],
        onClick: () => {
          push({
            pathname: `/users/${userId}/renewals`,
          });
        },
        disabled: false,
        btnLabel: '',
      },
      {
        disabled: false,
        btnText: 'Generate password ',
        hasRoles: [ROLES.DEV_ADMIN],
        onClick: () => setModalState(ModalKeys.PASSWORD, true),
        btnLabel: '',
      },
    ]);
  },
  [user, handleCreateWalletUser, userId, push]);

  useEffect(() => {
    setDetails([
      {
        label: 'Member Name',
        value: user?.firstName || '',
      },
      {
        label: 'Membership ID',
        value: user?.membershipId || '',
      },
      {
        label: 'Phone Number',
        value: user?.phoneNumber || '',
      },
      {
        label: 'Access Level',
        value: user?.accessLevel || '',
      },
      {
        label: 'Registration Source',
        value: user?.registrationSource || 'Unknown',
      },
      {
        label: 'Main Member',
        value: user?.isMainMember ? 'Yes' : 'No',
      },
      {
        label: 'Last Activity',
        value: user?.lastActivityDate || '_',
      },
      {
        label: 'App Version',
        value: user?.appVersion || '_',
      },
    ]);
  }, [user]);

  const UserInfo = (): JSX.Element => (
    <div>
      {
        details?.map((detail) => (
          <Card className={styles.infoCard}>
            <span className={styles.leftColumn}>{`${detail.label}:`}</span>
            <span className={styles.rightColumn}>{detail.value}</span>
          </Card>
        ))
      }
    </div>
  );

  const ActionsList = () => {
    if (!actions) {
      return null;
    }
    return (
      <div className={styles.actionCardsContainer}>
        {actions.filter((ac) => authStore.hasRoles(ac.hasRoles)).map((action) => (
          <Card className={styles.actionCard}>
            <span className={styles.actionBtnLabel}>{action.btnLabel}</span>
            <Button
              disabled={action.disabled}
              onClick={action.onClick}
              color="primary"
              variant="contained"
            >
              {action.btnText}
            </Button>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div>
      <UserInfo />
      <ActionsList />
      <AlertDialog
        title="Check KYC Status"
        content="Are you sure you want to check user's KYC status ?"
        isOpen={open ? open[ModalKeys.KYC] : false}
        agree={handleAgreeSync}
        disAgree={() => setModalState(ModalKeys.KYC, false)}
        hasActions
      />
      <UpdateMembershipIdModal
        loading={statisticsStore.loading || authStore?.isLoadingUpdateMembership}
        open={open ? open[ModalKeys.EDIT_USER] : false}
        handleUpdate={handleOpenModal}
        handleClose={() => setModalState(ModalKeys.EDIT_USER, false)}
      />
      <AlertDialog
        title={translate('delete')}
        content={translate('deleteAlert', {
        })}
        isOpen={open ? open[ModalKeys.DELETE_USER] : false}
        agree={() => deleteUserCnfm()}
        disAgree={() => setModalState(ModalKeys.DELETE_USER, false)}
        hasActions
      />
      <UpdateWalletPhoneNumber
        handleClose={() => setModalState(ModalKeys.UPDATE_PHONE, false)}
        modalOpen={open ? open[ModalKeys.UPDATE_PHONE] : false}
        userId={+userId}
      />
      <AddOnlineAccumulation
        handleClose={() => setModalState(ModalKeys.ACCUMULATION, false)}
        modalOpen={open ? open[ModalKeys.ACCUMULATION] : false}
        userId={+userId}
      />
      <UserPasswordModal
        handleClose={() => setModalState(ModalKeys.PASSWORD, false)}
        modalOpen={open ? open[ModalKeys.PASSWORD] : false}
        userId={+userId}
      />
    </div>
  );
};

export default UserDetails;
